import React, { useState } from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import styles from './AgendaView.module.scss'
import { AgendaItem, AgendaItemSource } from '../../../../../models/Agenda'
import { Button } from '../../../../../components/Button/Button'

interface EditAgendaItemInputProps {
  itemId: string
  initialText: string
  onUpdate: (itemId: string, text: string) => void
  onCancel: () => void
}

const EditAgendaItemInput: React.FC<EditAgendaItemInputProps> = ({ itemId, initialText, onUpdate, onCancel }) => {
  const [text, setText] = useState(initialText)

  const handleSave = async () => {
    if (!text.trim()) return
    await onUpdate(itemId, text.trim())
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSave()
    }
    if (e.key === 'Escape') {
      onCancel()
    }
  }

  return (
    <div className='bg-light d-flex flex-column zocks-gap-0.75 zocks-p-0.75 rounded'>
      <div className='d-flex align-items-center zocks-gap-0.75'>
        <input
          type='text'
          className='form-control flex-grow-1 bg-white zocks-border-gray-500 paragraph-medium'
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          autoFocus
        />
        <Button
          type='button'
          variant='borderless'
          className='p-0 d-flex align-items-center justify-content-center zocks-text-gray-500'
          aria-label='Cancel'
          onClick={onCancel}
        >
          <i className='bi bi-x-lg fs-6' />
        </Button>
      </div>
      <div className='d-flex justify-content-end zocks-gap-0.5'>
        <Button type='button' className='zocks-bg-gray-200 zocks-text-gray-900 button-small zocks-px-0.5 zocks-py-0.25' variant='borderless' onClick={onCancel}>
          Cancel
        </Button>
        <Button type='button' variant='primary' className='button-small zocks-px-0.5 zocks-py-0.25' onClick={handleSave} disabled={!text.trim()}>
          Save
        </Button>
      </div>
    </div>
  )
}

interface AddAgendaItemInputProps {
  onAdd: (text: string) => void
  onCancel?: () => void
  showCancel?: boolean
}

const AddAgendaItemInput: React.FC<AddAgendaItemInputProps> = ({ onAdd, onCancel, showCancel = true }) => {
  const [text, setText] = useState('')

  const handleAdd = () => {
    if (!text.trim()) return

    onAdd(text.trim())
    setText('')
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAdd()
    }
    if (e.key === 'Escape' && onCancel) {
      onCancel()
    }
  }

  return (
    <div className='w-100 mt-2'>
      <div className='bg-light d-flex align-items-center zocks-gap-1 p-2 rounded'>
        <input
          type='text'
          className='form-control flex-grow-1 bg-white zocks-border-gray-500 paragraph-medium-extended py-1 px-2'
          placeholder='Enter agenda item'
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          autoFocus
        />
        <Button
          type='button'
          variant='borderless'
          className='p-0 d-flex align-items-center justify-content-center zocks-text-gray-500'
          aria-label='Add item'
          onClick={handleAdd}
          disabled={!text.trim()}
        >
          <i className='bi bi-check fs-6' />
        </Button>
        {showCancel && onCancel && (
          <Button
            type='button'
            variant='borderless'
            className='p-0 d-flex align-items-center justify-content-center zocks-text-gray-500'
            aria-label='Cancel'
            onClick={onCancel}
          >
            <i className='bi bi-x-lg fs-6' />
          </Button>
        )}
      </div>
    </div>
  )
}

const EmptyAgendaState: React.FC<{ onAdd: (text: string) => void }> = ({ onAdd }) => (
  <li className='d-flex flex-column align-items-center justify-content-center text-center zocks-gap-0.75 py-3'>
    <div className='d-flex flex-column zocks-gap-0.5 align-items-center w-100'>
      <h6 className='mb-0 zocks-text-gray-900'>No agenda items yet.</h6>
      <p className='paragraph-small mb-0 zocks-text-gray-900'>
        <span>Add topics manually or adjust the </span>
        <span className='text-decoration-underline zocks-text-primary' role='button'>
          template
        </span>
        <span> to automatically generate agenda.</span>
      </p>
    </div>
    <AddAgendaItemInput onAdd={onAdd} showCancel={false} />
  </li>
)

interface AgendaItemSourceIconElementProps {
  source: AgendaItemSource
}

const AgendaItemSourceIconElement: React.FC<AgendaItemSourceIconElementProps> = ({ source }) => {
  if (source === AgendaItemSource.User) {
    return <i className='bi bi-hand-index-thumb zocks-text-gray-500' aria-label='user added' />
  }
  if (source === AgendaItemSource.Fixed) {
    return <i className='bi bi-list-ul zocks-text-gray-500' aria-label='fixed' />
  }
  if (source === AgendaItemSource.AIGenerated) {
    return <i className='bi bi-stars zocks-text-primary' aria-label='ai-generated' />
  }
  return <i className='bi bi-circle-fill' aria-label='other' />
}

interface AgendaItemSourceIconProps {
  itemId: string
  source: AgendaItemSource
}

const AgendaItemSourceIcon: React.FC<AgendaItemSourceIconProps> = ({ itemId, source }) => {
  const tooltipText =
    source === AgendaItemSource.User
      ? 'Added by User'
      : source === AgendaItemSource.Fixed
        ? 'Fixed topic from template'
        : source === AgendaItemSource.AIGenerated
          ? 'AI-generated from template'
          : null

  return tooltipText ? (
    <OverlayTrigger placement='top' overlay={<Tooltip id={`tooltip-${itemId}`}>{tooltipText}</Tooltip>}>
      <span className='me-3 d-flex align-items-center justify-content-center flex-shrink-0'>
        <AgendaItemSourceIconElement source={source} />
      </span>
    </OverlayTrigger>
  ) : (
    <span className='me-3 d-flex align-items-center justify-content-center flex-shrink-0'>
      <AgendaItemSourceIconElement source={source} />
    </span>
  )
}

interface UpcomingMeetingViewProps {
  items: AgendaItem[]
  onDelete: (agendaItemId: string) => void
  onAdd: (text: string) => void
  onEdit: (agendaItemId: string) => void
  onUpdate: (agendaItemId: string, text: string) => void
  onCancelEdit: () => void
  editingItemId: string | null
  showAddInput: boolean
  onCancelAdd: () => void
  onAddMore: () => void
}

export const UpcomingMeetingView: React.FC<UpcomingMeetingViewProps> = ({
  items,
  onDelete,
  onAdd,
  onEdit,
  onUpdate,
  onCancelEdit,
  editingItemId,
  showAddInput,
  onCancelAdd,
  onAddMore,
}) => {
  return (
    <>
      <ul className='list-unstyled mb-0'>
        {items.length === 0 && <EmptyAgendaState onAdd={onAdd} />}
        {items.map((item, index) => {
          const isEditing = editingItemId === item.id

          return (
            <li key={item.id} className={`position-relative ${index === items.length - 1 ? '' : 'mb-2'}`}>
              {isEditing ? (
                <EditAgendaItemInput itemId={item.id} initialText={item.text} onUpdate={onUpdate} onCancel={onCancelEdit} />
              ) : (
                <div className={`d-flex align-items-center bg-white zocks-border-0.25 zocks-border-gray-200 zocks-p-0.75 px-3 ${styles.agendaItem}`}>
                  <AgendaItemSourceIcon itemId={item.id} source={item.source} />
                  <span className='paragraph-medium flex-grow-1 zocks-text-gray-900'>{item.text}</span>
                  <div className={`d-flex align-items-center zocks-gap-0.5 ms-3 ${styles.actionButtons}`}>
                    <Button
                      type='button'
                      variant='borderless'
                      className='p-1 d-flex align-items-center justify-content-center zocks-text-gray-500'
                      aria-label='Edit item'
                      onClick={() => onEdit(item.id)}
                      disabled={editingItemId !== null}
                    >
                      <i className='bi bi-pencil fs-6' />
                    </Button>
                    <Button
                      type='button'
                      variant='borderless'
                      className='p-1 d-flex align-items-center justify-content-center zocks-text-gray-500'
                      aria-label='Delete item'
                      onClick={() => onDelete(item.id)}
                      disabled={editingItemId !== null}
                    >
                      <i className='bi bi-trash fs-6' />
                    </Button>
                  </div>
                </div>
              )}
            </li>
          )
        })}
        {showAddInput && <AddAgendaItemInput onAdd={onAdd} onCancel={onCancelAdd} />}
      </ul>
      {items.length > 0 && !showAddInput && (
        <div className='zocks-mt-1.25'>
          <Button variant='link' className='text-decoration-none p-0 zocks-text-primary-dark paragraph-small' onClick={onAddMore}>
            <i className='bi bi-plus-lg' />
            <span className='ms-1'>Add more</span>
          </Button>
        </div>
      )}
    </>
  )
}

export default UpcomingMeetingView
